import React, { createRef, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import Modal from "@motorata/base-ui.components.molecules.modal";
import { Box } from "@motorata/base-ui.components.atoms._atoms";
import { Label, P } from "@motorata/base-ui.components.atoms.typography";
import Button from "@motorata/base-ui.components.atoms.button";
import {
  CONFIRM_CODE_REGEX,
  EMAIL_REGEX,
  IS_ONE_DIGIT,
  PHONE_STRING_REGEX,
  testRegex
} from "@motorata/base-ui.utils.regex";
import Alert from "@motorata/base-ui.components.molecules.alert";
import Input from "@motorata/base-ui.components.atoms.input";
import Checkbox from "@motorata/base-ui.components.atoms.checkbox";
import { LeadService, PublicService } from "@motorata/base-ui.services.api-service";
import Link from "next/link";
import { removeSpacesFromPhoneNumber } from "@motorata/base-ui.utils.string";
import { AnalyticsService } from "@motorata/base-ui.services.segment-service";
import { SentryService } from "@motorata/base-ui.services.sentry-service";
import { TagManagerService } from "@motorata/base-ui.services.tag-manager-service";

const initialState = {
  1: "",
  2: "",
  3: "",
  4: ""
};

export const OfferModalLeadPhoneVerify = ({ id, type, brand, model, profile, client, lead, show, setShow, currentModal }) => {
  const arrLength = 4;
  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);
  const [elRefs, setElRefs] = useState([]);

  let analyticsParams : any = {
    productType: type,
    brand: brand,
    model: model,
  };

  if(profile){
    analyticsParams = {
      ...analyticsParams,
      profile: true,
      profileSlug: profile.slug,
      profileName: profile.name
    }
  }

  if(client){
    analyticsParams = {
      ...analyticsParams,
      client: true
    }
  }

  useEffect(() => {
    setElRefs([createRef(), createRef(), createRef(), createRef()]);
  }, [arrLength]);

  useEffect(() => {
    if (elRefs[0]) {
      elRefs[0].current?.focus();
    }
  }, [elRefs]);

  const [state, setState] = useState(initialState);

  const onChange = (e, number) => {
    const value = e.target.value;
    if (value.length === 1 && testRegex(value, IS_ONE_DIGIT)) {
      setState({
        ...state,
        [`${number}`]: value
      });
      if (number < 4) {
        elRefs[number].current.focus();
      }
    } else if (value.length === 4 && testRegex(value, CONFIRM_CODE_REGEX)) {
      setState({
        1: value[0],
        2: value[1],
        3: value[2],
        4: value[3],
      })
    } else if (value.length === 0) {
      setState({
        ...state,
        [`${number}`]: ""
      });
    }
  };

  const handleGoBack = useCallback(() => {
    setShow("lead");
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setSending(true);
      const value = await LeadService.verifyPhone(
        lead.id,
        `${state["1"]}${state["2"]}${state["3"]}${state["4"]}`
      );
      setSending(false);
      if (!value) {
        setError(true);
      } else {
        setError(false);
        AnalyticsService.track("Offer Lead Verify Phone", { ...analyticsParams });
        setShow("success");
      }
    } catch (e) {
      setError(true);
      SentryService.error("[ERROR LeadService.verifyPhone API]", e, { state, lead });
    } finally {
      setSending(false);
    }
  }, [state, lead]);

  useEffect(() => {
    if (currentModal === "success") {
      setState(initialState);
    }
  }, [currentModal]);

  return (
    <Modal title="Potwierdź swój numer teleofnu" show={show} setShow={setShow} maxWidth="500px">
      <P small textAlign="left">
        Wprowadź otrzymany kod SMS, który wysłaliśmy na numer telefonu <b>{lead?.phone}</b>, aby potwierdzić swój numer telefonu.
      </P>
      {error ? <Alert type="error" small>Niepoprawny kod weryfikacyjny</Alert> : <></>}
      <CodeWrapper>
        <input ref={elRefs[0]} value={state["1"]} onChange={e => onChange(e, 1)} />
        <input ref={elRefs[1]} value={state["2"]} onChange={e => onChange(e, 2)} />
        <input ref={elRefs[2]} value={state["3"]} onChange={e => onChange(e, 3)} />
        <input ref={elRefs[3]} value={state["4"]} onChange={e => onChange(e, 4)} />
      </CodeWrapper>
      <P small color="third">
        Nie otrzymałeś SMS-a z kodem? <button>Wyślij ponownie SMS</button>
      </P>
      <Button loading={sending} onClick={handleSubmit} submit width="100%">
        Zweryfikuj
      </Button>
      <Button width="100%" type="third" onClick={handleGoBack}>
        Wróć
      </Button>
    </Modal>
  );
};

const OfferModalLead = ({ id, type, brand, model, profile, client, currentModal, calculation, calculationParams, setLead, show, setShow }) => {
  const timeoutId = useRef<number | null>();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm();

  const number = watch("number");
  const company = watch("company");
  const checkbox = watch("checkbox");

  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyResponse, setCompanyResponse] = useState(null);

  // const isCompany = calculationParams?.type === "leasing";

  let analyticsParams : any = {
    productType: type,
    brand: brand,
    model: model,
  };

  if(profile){
    analyticsParams = {
      ...analyticsParams,
      profile: true,
      profileSlug: profile.slug,
      profileName: profile.name
    }
  }

  if(client){
    analyticsParams = {
      ...analyticsParams,
      client: true
    }
  }

  const onSubmit = useCallback(
    async el => {
      try {
        setSending(true);
        const body = {
          firstName: el.firstName,
          lastName: el.lastName,
          email: el.email,
          phone: removeSpacesFromPhoneNumber(el.phone),
        };

        const lead = await LeadService.addOffer(profile.id, id, body);
        TagManagerService.truck("lead_offer")
        AnalyticsService.track("Offer Lead Send", { ...analyticsParams });
        setLead(lead);
        // setShow("lead-phone-verify");
        setShow("success");
      } catch (e) {
        setError(true);
        SentryService.error("[ERROR LeadService.addOffer API]", e, el);
      } finally {
        setSending(false);
      }
    },
    [companyResponse]
  );

  const searchCompanyByNip = useCallback(async number => {
    try {
      setLoading(true);
      const res = await PublicService.getCompanyFromGUS(number);
      setValue("company", res);
      setCompanyResponse(res);
    } catch (e) {
      SentryService.error("[ERROR getCompanyFromGUS API]", e);
      setValue("company", {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // @ts-ignore
    timeoutId.current = setTimeout(() => {
      if (number) {
        searchCompanyByNip(number);
      }
    }, 1000);
  }, [number, searchCompanyByNip]);

  useEffect(() => {
    if (show) {
      AnalyticsService.track("Offer Lead Open", analyticsParams);
    }
  }, [show]);

  useEffect(() => {
    if (currentModal === "success") {
      reset();
    }
  }, [currentModal]);

  return (
    <Modal title="Otrzymaj bezpłatną ofertę" type="center" show={show} setShow={setShow} maxWidth="600px">
      <P small>
        Podaj niezbędne dane kontaktowe, a nasz doradca skontaktuje się
        z Tobą tak szybko jak to możliwe.
      </P>
      <Content onSubmit={handleSubmit(onSubmit)}>
        <Box marginBottom="M">
          <Label marginBottom="S">Dane osobowe</Label>
          <StyledGrid>
            <Input
              name="firstName"
              label="Imię*"
              error={errors.firstName}
              value={watch('firstName')}
              rhf={register("firstName", {
                required: true
              })}
            />
            <Input
              name="lastName"
              label="Nazwisko*"
              error={errors.lastName}
              value={watch('lastName')}
              rhf={register("lastName", {
                required: true
              })}
            />
          </StyledGrid>
          <StyledGrid>
            <Input
              name="phone"
              label="Telefon*"
              error={errors.phone}
              elementType="pattern"
              format="+48 ### ### ###"
              formattedValue={true}
              value={watch('phone')}
              rhf={register("phone", {
                required: true,
                pattern: {
                  value: PHONE_STRING_REGEX,
                  message: "Niepoprawny format telefonu. +48 123 456 789"
                }
              })}
            />
            <Input
              name="email"
              label="E-mail*"
              error={errors.email}
              value={watch('email')}
              rhf={register("email", {
                required: true,
                pattern: {
                  value: EMAIL_REGEX,
                  message: "Niepoprawny format email"
                }
              })}
            />
          </StyledGrid>
        </Box>
        {/*{isCompany && (*/}
        {/*  <Box marginBottom="M">*/}
        {/*    <Label marginBottom="S">Dane firmy</Label>*/}
        {/*    <Input*/}
        {/*      marginBottom="S"*/}
        {/*      name="number"*/}
        {/*      label="NIP*"*/}
        {/*      error={errors.number}*/}
        {/*      loading={loading}*/}
        {/*      elementType="pattern"*/}
        {/*      format="##########"*/}
        {/*      value={watch('number')}*/}
        {/*      rhf={register("number", {*/}
        {/*        required: true,*/}
        {/*        pattern: {*/}
        {/*          value: NIP_REGEX,*/}
        {/*          message: "Niepoprawna wartość"*/}
        {/*        }*/}
        {/*      })}*/}
        {/*    />*/}
        {/*    {company && (*/}
        {/*      <>*/}
        {/*        <Input*/}
        {/*          marginBottom="S"*/}
        {/*          name="company.name"*/}
        {/*          label="Nazwa firmy*"*/}
        {/*          error={(errors.company as any)?.name}*/}
        {/*          value={watch('company.name')}*/}
        {/*          rhf={register("company.name", {*/}
        {/*            required: true*/}
        {/*          })}*/}
        {/*        />*/}
        {/*        <Input*/}
        {/*          marginBottom="S"*/}
        {/*          name="company.address"*/}
        {/*          label="Adres*"*/}
        {/*          error={(errors.company as any)?.address}*/}
        {/*          value={watch('company.address')}*/}
        {/*          rhf={register("company.address", {*/}
        {/*            required: true*/}
        {/*          })}*/}
        {/*        />*/}
        {/*        <StyledGrid>*/}
        {/*          <Select*/}
        {/*            name="company.province"*/}
        {/*            label="Województwo*"*/}
        {/*            options={[*/}
        {/*              'Dolnośląskie',*/}
        {/*              'Kujawsko-pomorskie',*/}
        {/*              'Lubelskie',*/}
        {/*              'Lubuskie',*/}
        {/*              'Łódzkie',*/}
        {/*              'Małopolskie',*/}
        {/*              'Mazowieckie',*/}
        {/*              'Opolskie',*/}
        {/*              'Podkarpackie',*/}
        {/*              'Podlaskie',*/}
        {/*              'Pomorskie',*/}
        {/*              'Śląskie',*/}
        {/*              'Świętokrzyskie',*/}
        {/*              'Warmińsko-mazurskie',*/}
        {/*              'Wielkopolskie',*/}
        {/*              'Zachodniopomorskie'*/}
        {/*            ]}*/}
        {/*            error={(errors.company as any)?.province}*/}
        {/*            value={watch('company.province')}*/}
        {/*            rhf={register("company.province", {*/}
        {/*              required: true*/}
        {/*            })}*/}
        {/*          />*/}
        {/*          <Input*/}
        {/*            name="company.city"*/}
        {/*            label="Miasto*"*/}
        {/*            error={(errors.company as any)?.city}*/}
        {/*            value={watch('company.city')}*/}
        {/*            rhf={register("company.city", {*/}
        {/*              required: true*/}
        {/*            })}*/}
        {/*          />*/}
        {/*          <Input*/}
        {/*            name="company.postCode"*/}
        {/*            label="Kod pocztowy*"*/}
        {/*            elementType="pattern"*/}
        {/*            format="##-###"*/}
        {/*            formattedValue={true}*/}
        {/*            error={(errors.company as any)?.postCode}*/}
        {/*            value={watch('company.postCode')}*/}
        {/*            rhf={register("company.postCode", {*/}
        {/*              required: true,*/}
        {/*              pattern: {*/}
        {/*                value: POSTCODE_REGEX,*/}
        {/*                message: "Niepoprawna wartość"*/}
        {/*              }*/}
        {/*            })}*/}
        {/*          />*/}
        {/*        </StyledGrid>*/}
        {/*      </>*/}
        {/*    )}*/}
        {/*  </Box>*/}
        {/*)}*/}
        <Box marginBottom="M">
          <Label marginBottom="S">Informacje prawne</Label>
          <Checkbox
            name="checkbox"
            value={checkbox}
            error={errors.checkbox}
            rhf={register("checkbox", {
              required: true
            })}
          >
            *Oświadczam, że zapoznałem/-am się z <Link href="/regulamin">Regulaminem</Link> i <Link
            href="/polityka-prywatnosci">Polityką prywatności</Link> oraz akceptuję ich postanowienia.
          </Checkbox>
        </Box>
        <Button loading={sending} submit width="100%">
          Otrzymaj bezpłatną ofertę
        </Button>
      </Content>
    </Modal>
  );
};

export default OfferModalLead;

const CodeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${({ theme }) => theme.space.M} 0;

  input {
    background: ${({ theme }) => theme.color.background};
    color: ${({ theme }) => theme.color.secondary};
    font-weight: ${({ theme }) => theme.font.normal};
    width: 40px;
    height: 65px;
    border-radius: 3px;
    border: none;
    margin: 0 5px;
    text-align: center;
    font-size: 30px;
    padding: 0 !important;
  }
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${({ theme }) => theme.space.S};
  margin-bottom: ${({ theme }) => theme.space.S};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Content = styled.form``;
